import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Box, Typography, Grid, Button, 
  Divider, Pagination, useMediaQuery, 
  useTheme, Drawer, IconButton,
  Stack, Chip, InputAdornment, TextField,
  Container, Avatar, Badge
} from '@mui/material';
import { 
  Star, LocationOn, FilterList, Search,
  ChevronLeft, ChevronRight, Verified,
  Phone, WhatsApp, Facebook, Instagram,
  DirectionsCar
} from '@mui/icons-material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase-config';

const AllSalons = () => {
  const [dealers, setDealers] = useState([]);
  const [filteredDealers, setFilteredDealers] = useState([]);
  const [selectedCity, setSelectedCity] = useState('Сите');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const cities = [
    'Сите',
    'Скопје', 'Битола', 'Куманово', 'Прилеп', 'Тетово',
    'Велес', 'Штип', 'Охрид', 'Гостивар', 'Струмица',
    'Кавадарци', 'Кичево', 'Ресен', 'Гевгелија', 'Кочани',
    'Струга', 'Кратово', 'Крива Паланка', 'Делчево', 'Виница',
    'Берово', 'Македонска Каменица', 'Неготино', 'Пробиштип',
    'Богданци', 'Демир Капија', 'Демир Хисар', 'Крушево',
    'Македонски Брод', 'Пехчево', 'Радовиш', 'Свети Николе',
    'Валандово', 'Теарце', 'Желино', 'Арачиново', 'Сарај',
    'Липково', 'Чаир', 'Бутел', 'Гази Баба', 'Карпош',
    'Кисела Вода', 'Ѓорче Петров', 'Центар', 'Аеродром'
  ];
  

  useEffect(() => {
    const fetchDealers = async () => {
      try {
        const q = query(collection(db, 'users'), where('userType', '==', 'dealer'));
        const querySnapshot = await getDocs(q);
  
        const dealersData = await Promise.all(
          querySnapshot.docs.map(async (docSnap) => {
            const dealer = docSnap.data();
            const dealerId = docSnap.id;

            const adsQuery = query(
              collection(db, 'ads'),
              where('userId', '==', dealerId),
              where('status', '==', 'active') // само активни
            );
            const adsSnapshot = await getDocs(adsQuery);

            return {
              id: dealerId,
              dealerSlug: dealer.dealerSlug || '', // 👈 додади го ова!
              dealerName: dealer.dealerName || 'Неименуван Салон',
              city: dealer.city || '',
              logo: dealer.dealerLogo || dealer.avatar || 'https://via.placeholder.com/150',
              verified: dealer.verified || false,
              adsCount: adsSnapshot.size, // реален број
              phone: dealer.phone || '',
              whatsapp: dealer.whatsapp || '',
              facebook: dealer.facebook || '',
              instagram: dealer.instagram || ''
            };
          })
        );

        setDealers(dealersData);
        setFilteredDealers(dealersData);
      } catch (error) {
        console.error('Error fetching dealers:', error);
      }
    };

    fetchDealers();
  }, []);

  useEffect(() => {
    let result = [...dealers];
    
    if (selectedCity !== 'Сите') {
      result = result.filter(dealer => dealer.city === selectedCity);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(dealer => 
        dealer.dealerName.toLowerCase().includes(query) ||
        (dealer.city && dealer.city.toLowerCase().includes(query))
      );
    }

    result.sort((a, b) => (b.verified - a.verified) || (b.rating - a.rating));
    setFilteredDealers(result);
    setPage(1);
  }, [selectedCity, searchQuery, dealers]);

  const itemsPerPage = isMobile ? 6 : 12;
  const pageCount = Math.ceil(filteredDealers.length / itemsPerPage);
  const paginatedDealers = filteredDealers.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handleCityChange = (city) => {
    setSelectedCity(city);
    if (isMobile) setDrawerOpen(false);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ 
        display: 'flex', 
        minHeight: '100vh',
        bgcolor: 'background.default',
        gap: 3
      }}>
        {!isMobile && (
          <Box sx={{ 
            width: 300, 
            p: 3,
            borderRadius: '12px',
            bgcolor: 'background.paper',
            boxShadow: theme.shadows[1],
            position: 'sticky',
            top: 20,
            height: 'fit-content',
          }}>
            <Typography variant="h6" sx={{ 
              mb: 3, 
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <FilterList fontSize="medium" />
              Филтри
            </Typography>
            
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Пребарај салони..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                sx: { borderRadius: '8px' }
              }}
            />
            
            <Typography variant="subtitle1" sx={{ 
              mb: 2, 
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <LocationOn fontSize="small" />
              Градови
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {cities.map((city) => (
                <Chip
                  key={city}
                  label={city}
                  clickable
                  onClick={() => handleCityChange(city)}
                  variant={selectedCity === city ? 'filled' : 'outlined'}
                  color={selectedCity === city ? 'primary' : 'default'}
                  sx={{
                    borderRadius: '6px',
                    fontWeight: selectedCity === city ? '600' : '500',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ flex: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 4
          }}>
            <Typography variant="h5" sx={{ 
              fontWeight: '700', 
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <DirectionsCar />
              {selectedCity === 'Сите' ? 'Сите Авто Салони и Плацеви' : `Авто Салони и Плацеви во ${selectedCity}`}
              <Typography component="span" sx={{ 
                ml: 1, 
                color: 'text.secondary',
                fontWeight: '500'
              }}>
                ({filteredDealers.length})
              </Typography>
            </Typography>
            
            {isMobile && (
              <Button 
                variant="contained"
                startIcon={<FilterList />}
                onClick={toggleDrawer}
                sx={{ 
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: '600'
                }}
              >
                Филтри
              </Button>
            )}
          </Box>

          <Grid container spacing={3}>
            {paginatedDealers.length === 0 ? (
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  borderRadius: '12px',
                  bgcolor: 'background.paper'
                }}>
                  <Typography variant="body1">
                    Нема пронајдено салони за избраните филтри.
                  </Typography>
                </Box>
              </Grid>
            ) : (
              paginatedDealers.map((dealer) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={dealer.id}>
                  <SalonCard dealer={dealer} />
                </Grid>
              ))
            )}
          </Grid>

          {pageCount > 1 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 4
            }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                shape="rounded"
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '6px',
                    fontWeight: '500'
                  }
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
};

const SalonCard = ({ dealer }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '10px',
      overflow: 'hidden',
      bgcolor: 'background.paper',
      boxShadow: theme.shadows[1],
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[3]
      },
      maxWidth: 260,
      mx: 'auto'
    }}>
      {/* Logo and Name */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 2,
        pb: 1.5,
        bgcolor: 'background.default'
      }}>
        <Box sx={{
          width: 220,
          height: 120,
          mb: 1,
          borderRadius: '8px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f9f9f9',
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <img 
            src={dealer.logo} 
            alt={dealer.dealerName}
            style={{ 
              width: '100%', 
              height: '100%',
              objectFit: 'cover'
            }}
          />
        </Box>
        
        <Typography variant="subtitle1" sx={{ 
          fontWeight: '700',
          textAlign: 'center',
          mb: 0.5,
          fontSize: '0.95rem'
        }}>
          {dealer.dealerName}
        </Typography>
        
        {dealer.verified && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            color: 'success.main',
            mb: 0.5
          }}>
            <Verified sx={{ fontSize: '16px', mr: 0.5 }} />
            <Typography variant="caption" sx={{ fontWeight: '600' }}>
              Верифицирано
            </Typography>
          </Box>
        )}
      </Box>
    
      {/* Details */}
      <Box sx={{ px: 2, pb: 1, flex: 1 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          mb: 1
        }}>
          <LocationOn sx={{ 
            color: 'text.secondary',
            fontSize: 18,
            mr: 0.5 
          }} />
          <Typography variant="caption">
            {dealer.city || 'Неозначено'}
          </Typography>
        </Box>
    
        <Box sx={{ 
  display: 'flex', 
  alignItems: 'center',
  mb: 1
}}>
  <Typography variant="caption" color="text.secondary">
    {dealer.adsCount || 0} активни огласи
  </Typography>
</Box>
    
        {dealer.phone && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mb: 1
          }}>
            <Phone sx={{ 
              color: 'text.secondary',
              fontSize: 18,
              mr: 0.5 
            }} />
            <Typography variant="caption">
              {dealer.phone}
            </Typography>
          </Box>
        )}
      </Box>
    
      {/* Social and Action */}
      <Box sx={{ 
        px: 2,
        pb: 1.5,
        pt: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderTop: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {dealer.whatsapp && (
            <IconButton 
              size="small"
              href={`https://wa.me/${dealer.whatsapp}`}
              sx={{ color: 'success.main' }}
            >
              <WhatsApp fontSize="small" />
            </IconButton>
          )}
          {dealer.facebook && (
            <IconButton 
              size="small"
              href={dealer.facebook}
              target="_blank"
              sx={{ color: 'info.main' }}
            >
              <Facebook fontSize="small" />
            </IconButton>
          )}
          {dealer.instagram && (
            <IconButton 
              size="small"
              href={dealer.instagram}
              target="_blank"
              sx={{ color: 'secondary.main' }}
            >
              <Instagram fontSize="small" />
            </IconButton>
          )}
        </Box>
    
        <Button
          variant="outlined"
          color="primary"
          component={Link}
          to={`/${dealer.dealerSlug}`}
          size="small"
          sx={{ 
            borderRadius: '6px',
            fontWeight: '600',
            textTransform: 'none',
            fontSize: '0.75rem',
            px: 1.5,
            py: 0.5
          }}
        >
          Повеќе
        </Button>
      </Box>
    </Box>
    
  );
};

export default AllSalons;